import { routesMap } from 'app/Router';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  getCanvaSessionDraftId,
  getCanvaSessionSkuFilter,
  isCanvaSession,
} from 'utils/canvaSession';

/**
 * Whether the buyer can put the design they brought from Canva onto a different product, and how.
 *
 * <p>Only for a buyer who <em>chose</em> the product here: they arrived with a parked design,
 * were shown the products that fit it, and picked one — so picking again is a correction of
 * their own choice, and the design is still theirs to place. A Canva publish that names the
 * product itself (a brand template mapped to a SKU) has nothing to choose, and a buyer with no
 * parked design has nothing to carry over; both are left with the shop's ordinary way out.
 *
 * The parked design is the discriminator rather than the account type, because that is the thing
 * the switch actually needs: without it there is nothing to republish, and offering the button
 * would land the buyer on an empty job asking them to upload artwork they already have.
 */
export const useCanvaProductSwitch = (): {
  canSwitchProduct: boolean;
  switchProduct: () => void;
} => {
  const history = useHistory();
  // Read every render rather than memoised: the design is released when the job reaches the
  // cart, and the button has to stop offering it from that point on.
  const draftId = isCanvaSession() ? getCanvaSessionDraftId() : undefined;

  const switchProduct = useCallback(() => {
    const parkedDesign = isCanvaSession() ? getCanvaSessionDraftId() : undefined;
    if (!parkedDesign) {
      return;
    }
    // Back to the same landing the Canva hand-off produced -- not the bare gallery, which would
    // drop the design and start an empty job, and not the whole catalogue when the hand-off
    // named the products: those tags are what the buyer is choosing between.
    const restrictedTo = getCanvaSessionSkuFilter();
    const search = [
      restrictedTo,
      `canvaDraftId=${encodeURIComponent(parkedDesign)}`,
    ]
      .filter(Boolean)
      .join('&');
    history.push(`${routesMap.gallery.path}?${search}`);
  }, [history]);

  return { canSwitchProduct: Boolean(draftId), switchProduct };
};
